import { memo, FunctionComponent, useState } from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  Switch,
  TextField,
} from '@material-ui/core';
import { Add } from '@material-ui/icons';
import { User } from '../../../Users/types';
import ButtonProgress from '../../../../eliot-components/src/ButtonProgress/ButtonProgress';
import { useSnackbar } from 'notistack';
import { useUpdateUserMutation } from '../../../../store/api-slice';
import { useConfig } from '../../../../eliot-components/src/ConfigProvider/hooks';

interface Props {
  isOpen: boolean;
  onClose(): void;
  onUpdated(): void;
  user: User;
}

const UserRestrictedDialog: FunctionComponent<Props> = memo(
  ({ isOpen, user, onClose }) => {
    const [restricted, setRestricted] = useState(
      user.ipRestrictionEnabled || false
    );
    const [ipAddresses, setIpAddresses] = useState<string[]>(
      user.ipRestrictionAllowed ?? []
    );
    const [ipInput, setIpInput] = useState('');
    const [ipError, setIpError] = useState('');
    const { enqueueSnackbar } = useSnackbar();
    const [disableBtn, setDisableBtn] = useState(true);
    const [updateUser] = useUpdateUserMutation();
    const { apiBaseURL } = useConfig();
    const validateIpAddress = (ip: string): boolean => {
      // IPv4 validation regex
      const ipv4Regex =
        /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      // IPv6 validation regex (basic)
      const ipv6Regex = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^::1$|^::$/;
      // CIDR notation for IPv4
      const cidrRegex =
        /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\/(?:[0-9]|[1-2][0-9]|3[0-2])$/;

      return ipv4Regex.test(ip) || ipv6Regex.test(ip) || cidrRegex.test(ip);
    };

    const addIpAddress = () => {
      const trimmedInput = ipInput.trim();

      if (
        trimmedInput &&
        validateIpAddress(trimmedInput) &&
        !ipAddresses.includes(trimmedInput)
      ) {
        setIpAddresses([...ipAddresses, trimmedInput]);
        setIpInput('');
        setIpError('');
        setDisableBtn(false);
      } else {
        if (!trimmedInput) {
          setIpError('IP address cannot be empty');
        } else if (ipAddresses.includes(trimmedInput)) {
          setIpError('IP address already exists');
        } else if (!validateIpAddress(trimmedInput)) {
          setIpError(
            'Invalid IP address format. Use IPv4, IPv6, or CIDR notation'
          );
        }
      }
    };

    const handleSubmit = async () => {
      updateUser({
        data: {
          ipRestrictionEnabled: restricted,
          ipRestrictionAllowed: ipAddresses,
        },
        apiBaseURL,
        id: user.id,
      }).then(() => {
        enqueueSnackbar('User restrictions updated', { variant: 'info' });
        onClose();
      });
    };

    return (
      <>
        <Dialog aria-labelledby="dialog-title" open={isOpen} onClose={onClose}>
          <DialogTitle id="dialog-title">User IP Restrictions</DialogTitle>

          <DialogContent>
            <FormControlLabel
              control={
                <Switch
                  checked={restricted}
                  onChange={(e) => {
                    setDisableBtn(false);
                    setRestricted(e.target.checked);
                  }}
                  name="userRestricted"
                  color="primary"
                />
              }
              label="Enable IP Restrictions"
            />
            {restricted && (
              <>
                <br />
                <br />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      margin="dense"
                      variant="outlined"
                      id="ipAddress"
                      label="IP Address"
                      placeholder="Enter IP address (e.g., 192.168.1.1, 192.168.1.0/24)"
                      value={ipInput}
                      onChange={(e) => {
                        setIpInput(e.target.value);
                        if (ipError) setIpError('');
                      }}
                      error={!!ipError}
                      helperText={
                        ipError || 'Supports IPv4, IPv6, and CIDR notation'
                      }
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Add
                              color="primary"
                              onClick={addIpAddress}
                              style={{ cursor: 'pointer' }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {ipAddresses.length > 0 && (
                      <Grid container spacing={1} style={{ marginTop: 8 }}>
                        {ipAddresses.map((ip, index) => (
                          <Grid item key={index}>
                            <Chip
                              label={ip}
                              onDelete={() => {
                                setIpAddresses(
                                  ipAddresses.filter((item) => item !== ip)
                                );
                                setDisableBtn(false);
                              }}
                              color="primary"
                              variant="outlined"
                            />
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <ButtonProgress
              type="button"
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              isLoading={false}
              data-testid="submit-user-restrictions"
              disabled={disableBtn}
            >
              Submit
            </ButtonProgress>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default UserRestrictedDialog;
